import { useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  RefreshCw,
  RotateCw,
  Webhook,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  useWebhookFailureCount,
  useWebhookFailureDetail,
  useWebhookFailures,
  useWebhookRetry,
} from "@/hooks/use-webhook-failures";
import { cn } from "@/utils";
import type { WebhookFailureRecord } from "@/types/webhook-failures";

const FAILURE_KIND_LABELS: Record<string, string> = {
  invalid_url: "Invalid URL",
  auth_error: "Auth / headers",
  connect_error: "Connection error",
  timeout: "Timeout",
  request_error: "Request error",
  http_error: "HTTP error",
  unexpected_error: "Unexpected error",
};

function formatTimestamp(value?: string) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function failureKindLabel(kind?: string) {
  if (!kind) return "Unknown";
  return FAILURE_KIND_LABELS[kind] || kind;
}

function PayloadPreview({ payload }: { payload?: Record<string, unknown> | null }) {
  if (!payload) {
    return (
      <p className="text-sm text-muted-foreground">No payload available.</p>
    );
  }
  return (
    <pre className="max-h-64 overflow-auto rounded-md border bg-muted/40 p-3 text-xs">
      {JSON.stringify(payload, null, 2)}
    </pre>
  );
}

export default function WebhookFailuresPage() {
  const { failures, loading, error, refresh } = useWebhookFailures();
  const { total: failureTotal, refresh: refreshFailureCount } = useWebhookFailureCount(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { detail, loading: detailLoading, reload } = useWebhookFailureDetail(
    selectedId,
  );
  const { retry, loading: retrying, error: retryError } = useWebhookRetry();
  const [lastRetrySuccess, setLastRetrySuccess] = useState(false);

  const handleRetry = async () => {
    if (!selectedId) return;
    setLastRetrySuccess(false);
    try {
      const result = await retry(selectedId);
      if (result.success) {
        setLastRetrySuccess(true);
        setSelectedId(null);
        await refresh();
        await refreshFailureCount();
      } else {
        await reload(selectedId);
        await refresh();
        await refreshFailureCount();
      }
    } catch {
      await reload(selectedId);
      await refreshFailureCount();
    }
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Webhook failures</h1>
          {!loading && failureTotal > 0 && (
            <p className="mt-1 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{failureTotal}</span> pending
              {failureTotal !== failures.length
                ? ` (showing ${failures.length} in table)`
                : ""}
            </p>
          )}
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Customer status webhooks that failed after automatic retries. Inspect
            failure details and the payload that would be sent, then retry delivery
            from the dashboard.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            void refresh();
            void refreshFailureCount();
          }}
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Refresh
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {!loading && !error && failures.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-16 text-center">
          <CheckCircle2 className="h-10 w-10 text-muted-foreground/60" />
          <p className="text-sm font-medium">No failed webhooks</p>
          <p className="text-sm text-muted-foreground">
            Deliveries that exhaust retries or fail for other reasons will appear here.
          </p>
        </div>
      )}

      {(loading || failures.length > 0) && (
        <div className="overflow-hidden rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Case ID</th>
                <th className="px-4 py-3 text-left font-medium">Failure</th>
                <th className="px-4 py-3 text-left font-medium">HTTP</th>
                <th className="px-4 py-3 text-left font-medium">Attempts</th>
                <th className="px-4 py-3 text-left font-medium">Last failed</th>
                <th className="px-4 py-3 text-left font-medium">Manual retries</th>
              </tr>
            </thead>
            <tbody>
              {loading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b">
                    <td colSpan={6} className="px-4 py-3">
                      <div className="h-4 animate-pulse rounded bg-muted" />
                    </td>
                  </tr>
                ))}
              {!loading &&
                failures.map((row) => (
                  <FailureRow
                    key={row.document_id}
                    row={row}
                    onSelect={() => {
                      setLastRetrySuccess(false);
                      setSelectedId(row.document_id);
                    }}
                  />
                ))}
            </tbody>
          </table>
        </div>
      )}

      <Sheet
        open={!!selectedId}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
      >
        <SheetContent className="flex h-full max-h-[100dvh] w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-xl">
          <SheetHeader className="shrink-0 space-y-1 border-b px-6 py-4 text-left">
            <SheetTitle className="flex items-center gap-2">
              <Webhook className="h-5 w-5" />
              Webhook failure
            </SheetTitle>
          </SheetHeader>

          {detailLoading && !detail && (
            <div className="flex min-h-0 flex-1 items-center justify-center px-6 py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {detail?.failure && (
            <>
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Case ID</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <code className="rounded bg-muted px-2 py-0.5 text-xs">
                      {detail.failure.case_id}
                    </code>
                    <Link
                      to={`/cases?case_id=${encodeURIComponent(detail.failure.case_id)}`}
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      View case
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>
                </div>

                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Target URL</p>
                  <p className="break-all text-sm">{detail.failure.target_url || "—"}</p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Badge variant="destructive">
                    {failureKindLabel(detail.failure.failure_kind)}
                  </Badge>
                  {detail.failure.http_status_code != null && (
                    <Badge variant="outline">HTTP {detail.failure.http_status_code}</Badge>
                  )}
                  {detail.failure.attempts != null && (
                    <Badge variant="outline">{detail.failure.attempts} auto attempts</Badge>
                  )}
                </div>

                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Error message</p>
                  <p className="text-sm">{detail.failure.failure_message || "—"}</p>
                </div>

                {detail.failure.response_body_snippet && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">Response body</p>
                    <pre className="overflow-auto rounded-md border bg-muted/40 p-3 text-xs">
                      {detail.failure.response_body_snippet}
                    </pre>
                  </div>
                )}

                <Separator />

                <div className="space-y-2">
                  <p className="text-sm font-medium">Payload at failure</p>
                  <PayloadPreview payload={detail.failure.payload} />
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium">Current payload (will send on retry)</p>
                  <PayloadPreview payload={detail.current_payload ?? undefined} />
                </div>

                <p className="text-xs text-muted-foreground">
                  Last failed: {formatTimestamp(detail.failure.last_failed_at)}
                  {detail.failure.manual_retry_count
                    ? ` · Manual retries: ${detail.failure.manual_retry_count}`
                    : ""}
                </p>
              </div>

              <div className="shrink-0 space-y-3 border-t bg-background px-6 py-4">
                {retryError && (
                  <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    {retryError}
                  </div>
                )}

                {lastRetrySuccess && (
                  <div className="rounded-md border border-green-500/40 bg-green-500/10 px-3 py-2 text-sm text-green-800 dark:text-green-300">
                    Webhook delivered successfully.
                  </div>
                )}

                <Button
                  className="w-full"
                  onClick={() => void handleRetry()}
                  disabled={retrying || detailLoading}
                >
                  {retrying ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RotateCw className="mr-2 h-4 w-4" />
                  )}
                  Retry webhook
                </Button>
              </div>
            </>
          )}

          {!detailLoading && selectedId && !detail?.failure && (
            <p className="px-6 py-4 text-sm text-muted-foreground">
              Could not load failure details.
            </p>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function FailureRow({
  row,
  onSelect,
}: {
  row: WebhookFailureRecord;
  onSelect: () => void;
}) {
  return (
    <tr
      className={cn(
        "cursor-pointer border-b transition-colors hover:bg-muted/40",
      )}
      onClick={onSelect}
    >
      <td className="px-4 py-3 font-mono text-xs">{row.case_id}</td>
      <td className="px-4 py-3">
        <span className="text-muted-foreground">
          {failureKindLabel(row.failure_kind)}
        </span>
        {row.failure_message && (
          <p className="mt-0.5 max-w-xs truncate text-xs text-muted-foreground/80">
            {row.failure_message}
          </p>
        )}
      </td>
      <td className="px-4 py-3">{row.http_status_code ?? "—"}</td>
      <td className="px-4 py-3">{row.attempts ?? "—"}</td>
      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
        {formatTimestamp(row.last_failed_at)}
      </td>
      <td className="px-4 py-3">{row.manual_retry_count ?? 0}</td>
    </tr>
  );
}
